"use client";

import React, { memo, useMemo } from "react";

import { I18nUtil } from "@utils/i18n";
import { ShellLink } from "@components/ShellNavigation/ShellAwareLink";
import { ROUTER_PATH } from "@config/router";
import ReportHeader from "./ReportHeader";
import ScoreBreakdown from "./ScoreBreakdown";
import TokenInfo from "./TokenInfo";
import Translations from "@translate/components/Chat/Cards/XPayReportCard";
import { XPayReportCardProps } from "./XPayReportTypes";
import { cn } from "utils";
import isEqual from "react-fast-compare";

const I18n = I18nUtil.createScoped(Translations);

const XPayReportCardOrg: React.FC<XPayReportCardProps> = ({
  data,
  className,
}) => {
  const isHKStock = data?.price.currency !== "$";
  const tokenInfo = data?.token;
  const Tag =
    tokenInfo?.type === "token" ||
    tokenInfo?.type === "pool" ||
    tokenInfo?.type === "stock"
      ? ShellLink
      : "div";
  const routeMap: Record<string, string> = {
    stock: `${ROUTER_PATH.STOCK_DETAIL}/${tokenInfo?.name}`,
    token: `${ROUTER_PATH.CRYPTO_TOKEN}/${tokenInfo?.id}`,
  };
  const cardsView = useMemo(() => {
    if (!data) {
      return (
        <div className="flex max-md:flex-col items-center mt-6 bg-background_solid_neutral_primary rounded-xl p-5 max-md:p-4">
          <div className="text-content_secondary">
            {I18n.t("No data available")}
          </div>
        </div>
      );
    }

    const tokenSymbol = data.token.symbol;

    return (
      <div
        className={cn(
          "flex max-md:flex-col items-center mt-6 bg-background_solid_neutral_primary rounded-xl p-5 max-md:p-4",
          className,
        )}
      >
        <div
          className={cn(
            "flex max-md:flex-col max-md:gap-4 border-r max-md:border-r-none",
            "max-md:border-b border-border_white_transparent5 flex-1 items-center justify-between pr-5 min-w-0 max-md:w-full",
            "max-md:pb-4 relative max-md:pr-0",
          )}
        >
          <Tag
            className="flex flex-col gap-2 min-w-0 max-md:w-full"
            href={routeMap[tokenInfo?.type || "token"]}
          >
            <ReportHeader />
            <TokenInfo data={data} type={tokenInfo?.type} />
          </Tag>
          {data.forecastUrl && (
            <ShellLink
              aria-label="XPay Report Card"
              data-openreplay-label="chatbox_xpayreportcard_open"
              id="lnk-xpay-report-card"
              href={data.forecastUrl}
              target="_blank"
              className={cn(
                "button_m py-2 px-3 border-border_white_transparent20 text-content_primary border w-fit h-fit rounded-2",
                "max-md:absolute right-0 top-0",
                "max-md:py-[6px] max-md:px-[8px]",
                "inline-block text-center no-underline",
              )}
            >
              {!isHKStock
                ? I18n.t("%{var0} Forecast", {
                    var0: tokenSymbol,
                  })
                : I18n.t("View Detail")}
            </ShellLink>
          )}
        </div>
        <ScoreBreakdown score={data.score} />
      </div>
    );
  }, [data, className]);

  return cardsView;
};

// 自定义比较函数
const arePropsEqual = (
  prevProps: XPayReportCardProps,
  nextProps: XPayReportCardProps,
) => {
  // Only re-render if these props change
  return isEqual(prevProps, nextProps);
};

const XPayReportCard = memo(XPayReportCardOrg, arePropsEqual);
export default XPayReportCard;
